import React from 'react';
import {
  X,
  Phone,
  Calendar,
  Clock,
  User,
  FileText,
  CheckCircle,
  XCircle,
  Repeat,
  TrendingUp,
  RefreshCw,
  Mail,
  CreditCard,
  History,
  ArrowRight,
  Trash2,
  Pencil,
  DollarSign,
} from 'lucide-react';
import Modal from '../ui/Modal';
import BookingActivityTimeline from './BookingActivityTimeline';
import type { ManualRecurringGroup, OneTimeBooking } from '../../types/bookings';
import {
  formatDate,
  formatTime,
  formatCurrency,
  getStatusColor,
  getPaymentStatusColor,
} from '../../utils/bookingHelpers';

interface ManualRecurringBookingDetailsModalProps {
  group: ManualRecurringGroup | null;
  isOpen: boolean;
  onClose: () => void;
  onCancelSession?: (bookingId: number) => void;
  onRescheduleSession?: (bookingId: number) => void;
  onDeleteSession?: (bookingId: number) => void;
  onCancelAllSessions?: (groupId: string) => void;
  onDeleteGroup?: (groupId: string) => void;
  onEditSession?: (bookingId: number) => void;
  onBulkUpdatePrice?: (groupId: string, upcomingCount: number, totalCount: number) => void;
}

export default function ManualRecurringBookingDetailsModal({
  group,
  isOpen,
  onClose,
  onCancelSession,
  onRescheduleSession,
  onDeleteSession,
  onCancelAllSessions,
  onDeleteGroup,
  onEditSession,
  onBulkUpdatePrice,
}: ManualRecurringBookingDetailsModalProps) {
  // Early return if no group - must be before any other code that uses group
  if (!group) return null;

  const today = new Date().toISOString().split('T')[0];

  // Sort bookings by date
  const sortedBookings = [...group.bookings].sort((a, b) =>
    (a.booking_date || '').localeCompare(b.booking_date || '')
  );

  // Calculate progress
  const progress = group.total_sessions > 0
    ? Math.round((group.completed_sessions / group.total_sessions) * 100)
    : 0;

  // Find next session
  const nextSession = sortedBookings.find(
    (b) => b.booking_date && b.booking_date >= today && b.status !== 'cancelled'
  );

  // Find rescheduled sessions (those with original_booking_date set)
  const rescheduledSessions = sortedBookings.filter(
    (b) => b.original_booking_date || b.rescheduled_at
  );
  const hasRescheduledSessions = rescheduledSessions.length > 0;

  // Count upcoming sessions that can be cancelled
  const upcomingSessions = sortedBookings.filter(
    (b) => b.booking_date && b.booking_date >= today && b.status !== 'cancelled'
  );
  const nonCancelledSessions = sortedBookings.filter((b) => b.status !== 'cancelled');
  const hasUpcomingSessions = upcomingSessions.length > 0;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl" maxHeight="max-h-[95vh] sm:max-h-[90vh]" animate={false}>
        {/* Header */}
        <div className="sticky top-0 bg-green-50 border-b border-green-100 px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-2 sm:gap-3">
            <Repeat className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" />
            <div>
              <h2 className="text-lg sm:text-xl font-bold text-green-900">Manual Recurring Booking</h2>
              <p className="text-xs sm:text-sm text-green-700">{group.recurring_group_id}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 sm:w-10 sm:h-10 bg-white rounded-full flex items-center justify-center hover:bg-gray-100 transition-colors"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto">
        {/* Content */}
        <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
          {/* Status Badges */}
          <div className="flex gap-2 flex-wrap">
            <span className="px-3 py-1 rounded-lg text-xs sm:text-sm font-semibold bg-green-100 text-green-800 flex items-center gap-1">
              <User className="w-3 h-3" />
              Manual Booking
            </span>
            <span className="px-3 py-1 rounded-lg text-xs sm:text-sm font-semibold bg-purple-100 text-purple-800 flex items-center gap-1">
              <RefreshCw className="w-3 h-3" />
              {group.total_sessions} Sessions
            </span>
            {group.cancelled_sessions > 0 && (
              <span className="px-3 py-1 rounded-lg text-xs sm:text-sm font-semibold bg-red-100 text-red-800 border border-red-200 flex items-center gap-1">
                <XCircle className="w-3 h-3" />
                {group.cancelled_sessions} of {group.total_sessions} cancelled
              </span>
            )}
            {/* Total refunds badge */}
            {(() => {
              const totalRefund = group.bookings.reduce((sum, b) => sum + (Number(b.refund_amount) || 0), 0);
              return totalRefund > 0 ? (
                <span className="px-3 py-1 rounded-lg text-xs sm:text-sm font-semibold bg-purple-100 text-purple-800 border border-purple-200 flex items-center gap-1">
                  Refunded: {formatCurrency(totalRefund)}
                </span>
              ) : null;
            })()}
          </div>

          {/* Progress Bar */}
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-3 sm:p-4 border border-green-200">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2 sm:mb-3">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2 text-sm sm:text-base">
                <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
                Progress
              </h3>
              <span className="text-xs sm:text-sm text-gray-600">
                {group.completed_sessions} / {group.total_sessions} sessions
              </span>
            </div>
            <div className="w-full bg-white rounded-full h-2.5 sm:h-3 overflow-hidden shadow-inner">
              <div
                className="bg-gradient-to-r from-green-500 to-emerald-600 h-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
            <p className="text-xs sm:text-sm text-gray-600 mt-2">{progress}% completed</p>
          </div>

          {/* Customer Info */}
          <div className="bg-gray-50 rounded-2xl p-4 sm:p-5">
            <h4 className="font-bold text-gray-900 mb-3 sm:mb-4 flex items-center gap-2 text-sm sm:text-base">
              <User className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
              Customer Information
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div>
                <p className="text-xs sm:text-sm text-gray-500 mb-1">Name</p>
                <p className="text-gray-900 font-semibold text-sm sm:text-base">{group.customer_name}</p>
              </div>
              {group.customer_email && (
                <div>
                  <p className="text-xs sm:text-sm text-gray-500 mb-1">Email</p>
                  <a
                    href={`mailto:${group.customer_email}`}
                    className="text-green-600 hover:text-green-700 font-semibold text-xs sm:text-sm break-all flex items-center gap-1"
                  >
                    <Mail className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    {group.customer_email}
                  </a>
                </div>
              )}
              {group.customer_phone && (
                <div>
                  <p className="text-xs sm:text-sm text-gray-500 mb-1">Phone</p>
                  <a
                    href={`tel:${group.customer_phone}`}
                    className="text-green-600 hover:text-green-700 flex items-center gap-1.5 font-semibold text-sm sm:text-base"
                  >
                    <Phone className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    {group.customer_phone}
                  </a>
                </div>
              )}
            </div>
          </div>

          {/* Booking Details */}
          <div className="bg-gray-50 rounded-2xl p-4 sm:p-5">
            <h3 className="font-bold text-gray-900 mb-3 sm:mb-4 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-purple-600" />
              Booking Details
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div>
                <p className="text-sm text-gray-500 mb-1">Court</p>
                <p className="text-base font-semibold text-gray-900">{group.court_name}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Sport</p>
                <p className="text-base font-semibold text-gray-900">{group.sport_name}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">First Session</p>
                <p className="text-base font-semibold text-gray-900">{formatDate(group.first_session_date)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Last Session</p>
                <p className="text-base font-semibold text-gray-900">{formatDate(group.last_session_date)}</p>
              </div>
            </div>
          </div>

          {/* Next Session Highlight */}
          {nextSession && (
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-4 border-2 border-green-300">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-600 rounded-full flex items-center justify-center">
                    <Clock className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-green-700 font-semibold">Next Session</p>
                    <p className="text-lg font-bold text-gray-900">
                      {formatDate(nextSession.booking_date)} • {formatTime(nextSession.start_time)} - {formatTime(nextSession.end_time)}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Payment Info */}
          <div className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200 rounded-2xl p-4 sm:p-5">
            <h3 className="font-bold text-green-900 mb-3 sm:mb-4 flex items-center gap-2">
              <CreditCard className="w-5 h-5" />
              Payment Summary
            </h3>
            <div className="space-y-3 sm:space-y-4">
              <div className="bg-white rounded-xl p-3 sm:p-4">
                <p className="text-sm text-green-600 mb-1">Total Revenue</p>
                <p className="text-green-900 font-black text-2xl sm:text-3xl">{formatCurrency(group.total_amount)}</p>
              </div>
              <div className="bg-white rounded-xl p-3 sm:p-4">
                <p className="text-sm text-gray-600 mb-1">Payment Method</p>
                <p className="text-gray-900 font-bold text-lg capitalize">
                  {sortedBookings[0]?.payment_method || 'Cash/Transfer/POS'}
                </p>
              </div>
              <p className="text-xs text-green-700 italic">
                * Manual bookings - payment received directly at venue
              </p>
            </div>
          </div>

          {/* Reschedule History */}
          {hasRescheduledSessions && (
            <div className="bg-blue-50 border-2 border-blue-200 rounded-2xl p-4 sm:p-5">
              <h4 className="font-bold text-blue-900 mb-3 sm:mb-4 flex items-center gap-2">
                <History className="w-5 h-5" />
                Reschedule History
              </h4>
              <div className="space-y-3">
                {rescheduledSessions.map((booking, idx) => {
                  const sessionNumber = sortedBookings.findIndex((b) => b.id === booking.id) + 1;
                  return (
                    <div key={booking.id}>
                      <div className="bg-white rounded-xl p-3 sm:p-4">
                        <p className="text-sm text-blue-600 font-semibold mb-2">
                          Session #{sessionNumber} - {booking.booking_reference}
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                          <div className="flex items-center gap-2">
                            <div className="flex-1">
                              <p className="text-gray-500 text-xs mb-1">Original Date</p>
                              <p className="text-gray-600 font-semibold line-through">
                                {booking.original_booking_date
                                  ? formatDate(booking.original_booking_date)
                                  : 'N/A'}
                              </p>
                              {(booking.original_start_time && booking.original_end_time) && (
                                <p className="text-gray-500 text-xs line-through">
                                  {formatTime(booking.original_start_time)} - {formatTime(booking.original_end_time)}
                                </p>
                              )}
                            </div>
                            <ArrowRight className="w-4 h-4 text-blue-400 flex-shrink-0" />
                            <div className="flex-1">
                              <p className="text-gray-500 text-xs mb-1">New Date</p>
                              <p className="text-gray-900 font-semibold">
                                {formatDate(booking.booking_date)}
                              </p>
                              <p className="text-gray-600 text-xs">
                                {formatTime(booking.start_time)} - {formatTime(booking.end_time)}
                              </p>
                            </div>
                          </div>
                        </div>
                        {booking.reschedule_reason && (
                          <div className="mt-2 pt-2 border-t border-blue-100">
                            <p className="text-xs text-blue-600 font-medium">Reason:</p>
                            <p className="text-xs text-blue-800">{booking.reschedule_reason}</p>
                          </div>
                        )}
                        {booking.rescheduled_at && (
                          <p className="text-xs text-gray-400 mt-2">
                            Rescheduled on {new Date(booking.rescheduled_at).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                      {idx < rescheduledSessions.length - 1 && (
                        <div className="flex items-center justify-center my-2">
                          <div className="w-1 h-4 bg-blue-200 rounded" />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* All Sessions List - Swipeable Horizontal */}
          {sortedBookings.length > 0 && (
            <div className="bg-white rounded-2xl border-2 border-gray-200 p-4 sm:p-5">
              <h4 className="font-bold text-gray-900 mb-3 sm:mb-4 flex items-center gap-2 text-sm sm:text-base">
                <FileText className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />
                All Sessions ({sortedBookings.length})
              </h4>

              {/* Swipeable horizontal scroll container */}
              <div className="overflow-x-auto scrollbar-hide -mx-4 sm:-mx-5 px-4 sm:px-5">
                <div className="flex gap-2 sm:gap-3 pb-2">
                  {sortedBookings.map((booking, index) => {
                    const statusColor = getStatusColor(booking.status);
                    const isCancelled = booking.status === 'cancelled';
                    const isCompleted = booking.status === 'completed' ||
                      (booking.booking_date && booking.booking_date < today && !isCancelled);
                    const isScheduled = ['confirmed', 'paid'].includes(booking.status) &&
                      booking.booking_date && booking.booking_date >= today;

                    // Check if session can be cancelled/rescheduled
                    const bookingDateTime = new Date(`${booking.booking_date}T${booking.start_time}`);
                    const now = new Date();
                    const canModify = !isCancelled && bookingDateTime > now;

                    return (
                      <div
                        key={booking.id}
                        className={`min-w-[280px] rounded-xl border-2 p-4 transition-all ${
                          isCancelled ? 'bg-red-50 border-red-200' :
                          isCompleted ? 'bg-green-50 border-green-200' :
                          isScheduled ? 'bg-blue-50 border-blue-200' :
                          'bg-gray-50 border-gray-200'
                        }`}
                      >
                        {/* Session Header */}
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                              isCancelled ? 'bg-red-200 text-red-700' :
                              isCompleted ? 'bg-green-200 text-green-700' :
                              'bg-blue-200 text-blue-700'
                            }`}>
                              #{index + 1}
                            </span>
                            <span className={`px-2 py-1 rounded text-xs font-semibold ${statusColor}`}>
                              {booking.status.replace('_', ' ').toUpperCase()}
                            </span>
                          </div>

                          {/* Action buttons for upcoming sessions */}
                          <div className="flex gap-1">
                            {/* Edit button */}
                            {canModify && onEditSession && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onEditSession(booking.id);
                                }}
                                className="px-2 py-1 bg-yellow-100 hover:bg-yellow-200 text-yellow-700 rounded-lg flex items-center gap-1 transition-colors text-xs font-semibold"
                                title="Edit this session"
                              >
                                <Pencil className="w-3 h-3" />
                              </button>
                            )}

                            {/* Reschedule button */}
                            {canModify && onRescheduleSession && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onRescheduleSession(booking.id);
                                }}
                                className="px-2 py-1 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-lg flex items-center gap-1 transition-colors text-xs font-semibold"
                                title="Reschedule this session"
                              >
                                <RefreshCw className="w-3 h-3" />
                              </button>
                            )}

                            {/* Cancel button */}
                            {canModify && onCancelSession && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onCancelSession(booking.id);
                                }}
                                className="px-2 py-1 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg flex items-center gap-1 transition-colors text-xs font-semibold"
                                title="Cancel this session"
                              >
                                <XCircle className="w-3 h-3" />
                              </button>
                            )}
                          </div>
                        </div>

                        {/* Session Details */}
                        <div className="space-y-2">
                          <div>
                            <p className="text-xs text-gray-600 mb-1">Date</p>
                            <p className="text-sm font-bold text-gray-900">
                              {formatDate(booking.booking_date)}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-600 mb-1">Time</p>
                            <p className="text-sm font-bold text-gray-900">
                              {formatTime(booking.start_time)} - {formatTime(booking.end_time)}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-600 mb-1">Amount</p>
                            <p className="text-sm font-bold text-gray-900">
                              {formatCurrency(parseFloat(String(booking.venue_share || booking.final_price || 0)))}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-600 mb-1">Reference</p>
                            <p className="text-xs text-gray-700 font-mono">
                              {booking.booking_reference}
                            </p>
                          </div>

                          {/* Check-in info */}
                          {booking.checked_in_at && (
                            <div className="border-t border-green-200 pt-2 mt-2">
                              <p className="text-xs text-green-600 flex items-center gap-1">
                                <CheckCircle className="w-3 h-3" />
                                Checked In
                              </p>
                            </div>
                          )}

                          {/* Cancellation Info */}
                          {isCancelled && booking.cancelled_at && (
                            <div className="border-t border-red-200 pt-2 mt-2">
                              <p className="text-xs text-red-600 mb-1">Cancelled</p>
                              {booking.cancellation_reason && (
                                <p className="text-xs text-red-900 bg-red-100 rounded p-2">
                                  {booking.cancellation_reason}
                                </p>
                              )}
                              {/* Refund info for cancelled session */}
                              {booking.refund_type && (
                                <div className="mt-2 bg-purple-50 rounded p-2">
                                  <p className="text-xs text-purple-700 font-semibold">
                                    {booking.refund_type === 'full' ? 'Full Refund' : booking.refund_type === 'partial' ? 'Partial Refund' : 'No Refund'}
                                    {Number(booking.refund_amount) > 0 && (
                                      <span className="ml-1">— {formatCurrency(booking.refund_amount || 0)}</span>
                                    )}
                                  </p>
                                </div>
                              )}
                            </div>
                          )}

                          {/* Notes */}
                          {booking.manual_booking_notes && (
                            <div className="border-t border-gray-200 pt-2 mt-2">
                              <p className="text-xs text-gray-600 mb-1">Notes</p>
                              <p className="text-xs text-gray-900 bg-gray-100 rounded p-2">
                                {booking.manual_booking_notes}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {sortedBookings.length > 3 && (
                <p className="text-xs text-gray-500 mt-3 text-center">← Swipe to see all sessions →</p>
              )}
            </div>
          )}

          {/* Pack history (activity.view) — spans every session in the group */}
          {sortedBookings[0]?.venue_id && (
            <BookingActivityTimeline
              venueId={sortedBookings[0].venue_id}
              recurringGroupId={group.recurring_group_id}
            />
          )}
        </div>
        </div>

        {/* Footer Actions */}
        <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex flex-col sm:flex-row gap-3 justify-between">
          <button
            onClick={onClose}
            className="w-full sm:w-auto bg-gray-100 text-gray-900 py-3 rounded-xl font-semibold hover:bg-gray-200 transition-colors px-8"
          >
            Close
          </button>

          <div className="flex flex-col sm:flex-row gap-3">
            {/* Bulk price update — only makes sense when there are non-cancelled sessions */}
            {nonCancelledSessions.length > 0 && onBulkUpdatePrice && (
              <button
                onClick={() => onBulkUpdatePrice(
                  group.recurring_group_id,
                  upcomingSessions.length,
                  nonCancelledSessions.length
                )}
                className="w-full sm:w-auto bg-orange-500 hover:bg-orange-600 text-white py-3 rounded-xl font-semibold transition-colors px-8 flex items-center justify-center gap-2"
              >
                <DollarSign className="w-5 h-5" />
                Update Group Price
              </button>
            )}

            {/* Delete Recurring Pack Button */}
            {/* TEMPORARY: shows when no upcoming sessions remain (past sessions treated as done) */}
            {/* To revert to strict mode: replace !hasUpcomingSessions with group.cancelled_sessions === group.total_sessions */}
            {!hasUpcomingSessions && onDeleteGroup && (
              <button
                onClick={() => onDeleteGroup(group.recurring_group_id)}
                className="w-full sm:w-auto bg-red-600 hover:bg-red-700 text-white py-3 rounded-xl font-semibold transition-colors px-8 flex items-center justify-center gap-2"
              >
                <Trash2 className="w-5 h-5" />
                Delete Recurring Pack
              </button>
            )}

            {/* Cancel All Sessions Button - shows when there are upcoming sessions */}
            {hasUpcomingSessions && onCancelAllSessions && (
              <button
                onClick={() => onCancelAllSessions(group.recurring_group_id)}
                className="w-full sm:w-auto bg-red-600 hover:bg-red-700 text-white py-3 rounded-xl font-semibold transition-colors px-8 flex items-center justify-center gap-2"
              >
                <XCircle className="w-5 h-5" />
                Cancel All Sessions ({upcomingSessions.length})
              </button>
            )}
          </div>
        </div>
    </Modal>
  );
}
